/**
 * Skeleton Loading Components
 *
 * Provides various skeleton loading states with the app's lantern logo
 * for a branded loading experience.
 */

import React from 'react';
import './Skeleton.css';

/**
 * LanternLoader - Centered loading indicator with glowing lantern
 */
export function LanternLoader({ size = 'medium', text = 'Loading...' }) {
  const sizeClass = size === 'small' ? 'small' : size === 'large' ? 'large' : '';

  return (
    <div className="loading-lantern">
      <img
        src="/images/btm-circle-logo.png"
        alt="Loading"
        className={`loading-lantern-icon ${sizeClass}`}
      />
      {text && <span className="loading-lantern-text">{text}</span>}
    </div>
  );
}

/**
 * InlineLoader - Small inline loading indicator
 */
export function InlineLoader({ text }) {
  return (
    <span className="loading-inline">
      <img
        src="/images/btm-circle-logo.png"
        alt=""
        className="loading-inline-lantern"
      />
      {text && <span>{text}</span>}
    </span>
  );
}

/**
 * PageLoader - Full page loading state
 */
export function PageLoader({ text = 'Loading...' }) {
  return (
    <div className="loading-page">
      <img
        src="/images/btm-circle-logo.png"
        alt="Loading"
        className="loading-lantern-icon"
      />
      <span className="loading-lantern-text">{text}</span>
    </div>
  );
}

/**
 * LoadingOverlay - Overlay for modals/sections during loading
 */
export function LoadingOverlay({ text }) {
  return (
    <div className="loading-overlay">
      <LanternLoader size="small" text={text} />
    </div>
  );
}

/**
 * SkeletonText - Text placeholder
 */
export function SkeletonText({ width = 'medium' }) {
  return <div className={`skeleton skeleton-text ${width}`} />;
}

/**
 * SkeletonTitle - Title placeholder (larger)
 */
export function SkeletonTitle() {
  return <div className="skeleton skeleton-title" />;
}

/**
 * SkeletonThumbnail - Thumbnail placeholder
 */
export function SkeletonThumbnail() {
  return <div className="skeleton skeleton-thumbnail" />;
}

/**
 * SkeletonAvatar - Avatar placeholder
 */
export function SkeletonAvatar() {
  return <div className="skeleton skeleton-avatar" />;
}

/**
 * SkeletonButton - Button placeholder
 */
export function SkeletonButton() {
  return <div className="skeleton skeleton-button" />;
}

/**
 * SkeletonReflectionCard - Full reflection card skeleton
 */
export function SkeletonReflectionCard() {
  return (
    <div className="skeleton-card">
      <div className="skeleton-card-content">
        <div className="skeleton skeleton-text medium" />
        <div className="skeleton skeleton-text short" />
      </div>
      <div className="skeleton-card-actions">
        <div className="skeleton skeleton-button" />
      </div>
      <div className="skeleton skeleton-thumbnail" />
    </div>
  );
}

/**
 * SkeletonPromptCard - Full prompt card skeleton
 */
export function SkeletonPromptCard({ showReflections = true }) {
  return (
    <div className="skeleton-prompt-card">
      <div className="skeleton skeleton-text long" />
      <div className="skeleton skeleton-text medium" />
      {showReflections && (
        <div className="skeleton-prompt-reflections">
          <SkeletonReflectionCard />
          <SkeletonReflectionCard />
        </div>
      )}
    </div>
  );
}

/**
 * SkeletonList - List of skeleton items
 */
export function SkeletonList({ count = 3, children }) {
  return (
    <div className="skeleton-list">
      {Array.from({ length: count }).map((_, i) => (
        <div key={i} className="skeleton-list-item">
          {children || <SkeletonReflectionCard />}
        </div>
      ))}
    </div>
  );
}

// Default export for convenience
export default {
  LanternLoader,
  InlineLoader,
  PageLoader,
  LoadingOverlay,
  SkeletonText,
  SkeletonTitle,
  SkeletonThumbnail,
  SkeletonAvatar,
  SkeletonButton,
  SkeletonReflectionCard,
  SkeletonPromptCard,
  SkeletonList,
};
